import { parseBookingRequirements } from '../components/VenueDashboard/utils/bookingAddOns';
import { bookingHeadcount } from './bookingHeadcount';
import { roundMoney } from './bookingAmounts';

export const isPerPersonBookingType = (bookingType) => {
  const t = String(bookingType || '').toLowerCase();
  return t === 'table' || t === 'per_person' || t === 'per-person';
};

export const normalizeBookingTypeForUi = (raw) => {
  const t = String(raw || '').toLowerCase();
  if (t === 'full-venue' || t === 'full_venue' || t === 'exclusive') return 'full-venue';
  if (t === 'table' || t === 'per_person' || t === 'per-person') return 'table';
  return 'table';
};

export const normalizeBookingModeApi = (bookingType) =>
  isPerPersonBookingType(bookingType) ? 'per_person' : 'exclusive';

const isExplicitlyDisabled = (value) =>
  value === false || value === 0 || value === 'false' || value === '0';

/** Whether per-person (table) bookings are offered for this venue. Defaults to true when unset. */
export const isPerPersonBookingEnabled = (bookingRules) => {
  if (bookingRules == null) return true;
  if (isExplicitlyDisabled(bookingRules.per_person_enabled)) return false;
  if (isExplicitlyDisabled(bookingRules.perPersonEnabled)) return false;
  return true;
};

export const resolveDefaultBookingType = (bookingRules) =>
  (isPerPersonBookingEnabled(bookingRules) ? 'table' : 'full-venue');

export const coerceBookingTypeForRules = (bookingType, bookingRules) => {
  if (!isPerPersonBookingEnabled(bookingRules)) return 'full-venue';
  return normalizeBookingTypeForUi(bookingType);
};

const parsePositiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const parseNonNegativeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Resolve UI booking type from stored booking + requirements. */
export const resolveBookingTypeFromRecord = (booking, req = null) => {
  const parsedReq = req || parseBookingRequirements(booking);
  const raw =
    parsedReq?.booking_type ||
    parsedReq?.pricing_mode ||
    booking?.booking_type ||
    booking?.booking_mode;
  if (String(booking?.booking_mode || '').toLowerCase() === 'per_person') {
    return 'table';
  }
  return normalizeBookingTypeForUi(raw);
};

/** Read persisted pricing metadata from a booking record. */
export const readPricingMetadata = (booking, req = null) => {
  const parsedReq = req || parseBookingRequirements(booking);
  const bookingType = resolveBookingTypeFromRecord(booking, parsedReq);
  const pricingMode =
    parsedReq?.pricing_mode ||
    normalizeBookingModeApi(bookingType) ||
    (String(booking?.booking_mode || '').toLowerCase() === 'per_person' ? 'per_person' : 'exclusive');

  const headcount = bookingHeadcount(booking, parsedReq);

  const perPersonRate = parsePositiveNumber(
    parsedReq?.per_person_rate ??
      parsedReq?.perPersonRate ??
      booking?.per_person_rate ??
      (pricingMode === 'per_person' ? booking?.hourly_rate : null)
  );
  const spaceHourlyRate = parsePositiveNumber(
    parsedReq?.space_hourly_rate ??
      parsedReq?.spaceHourlyRate ??
      parsedReq?.base_hourly_rate ??
      parsedReq?.base_price_per_hour ??
      (pricingMode === 'exclusive' ? booking?.hourly_rate : null)
  );
  const baseFee = parseNonNegativeNumber(parsedReq?.base_fee ?? booking?.total_amount);

  return {
    pricingMode,
    bookingType,
    headcount: pricingMode === 'per_person' ? headcount ?? 1 : headcount,
    perPersonRate,
    spaceHourlyRate,
    baseFee
  };
};

/** Build pricing metadata object to persist in requirements. */
export const buildPricingMetadata = ({
  bookingType,
  headcount: _headcount,
  duration,
  perPersonRate,
  spaceHourlyRate,
  baseFee
}) => {
  const pricingMode = normalizeBookingModeApi(bookingType);

  return {
    pricing_mode: pricingMode,
    booking_type: bookingType,
    per_person_rate: pricingMode === 'per_person' ? perPersonRate ?? null : null,
    space_hourly_rate: pricingMode === 'exclusive' ? spaceHourlyRate ?? null : null,
    base_hourly_rate: spaceHourlyRate ?? null,
    base_fee: baseFee != null ? roundMoney(baseFee) : null,
    duration_hours: duration > 0 ? duration : null
  };
};

/** Compute venue hire (excl. add-ons) from stored pricing metadata. */
export const computeBaseFromPricingMetadata = ({
  pricingMode,
  headcount,
  duration,
  perPersonRate,
  spaceHourlyRate
}) => {
  if (pricingMode === 'per_person') {
    if (perPersonRate == null || perPersonRate <= 0 || !duration || duration <= 0) return null;
    const guests = Math.max(1, Number.parseInt(headcount, 10) || 1);
    return roundMoney(perPersonRate * guests * duration);
  }
  if (!duration || duration <= 0) return null;
  if (spaceHourlyRate == null || spaceHourlyRate <= 0) return null;
  return roundMoney(spaceHourlyRate * duration);
};

/**
 * Stored per-person rate on the booking. Do not invent a rate from amount ÷ guests ÷ hours.
 * Fall back to bookings.hourly_rate only when this booking is actually per-person —
 * that column is reused for exclusive hourly on full-venue hires.
 */
export const resolveConfirmedPerPersonRate = ({ booking, req = null }) => {
  const parsedReq = req || parseBookingRequirements(booking);
  const explicit = parsePositiveNumber(
    parsedReq?.per_person_rate ?? parsedReq?.perPersonRate ?? booking?.per_person_rate
  );
  if (explicit != null) return explicit;
  const mode = String(
    parsedReq?.pricing_mode || booking?.booking_mode || parsedReq?.booking_type || ''
  ).toLowerCase();
  if (mode === 'per_person' || mode === 'table' || mode === 'per-person') {
    return parsePositiveNumber(booking?.hourly_rate);
  }
  return null;
};

/**
 * Stored exclusive hourly rate on the booking. Do not invent a rate from amount ÷ hours.
 */
export const resolveConfirmedSpaceHourlyRate = ({ booking, req = null }) => {
  const parsedReq = req || parseBookingRequirements(booking);
  return parsePositiveNumber(
    parsedReq?.space_hourly_rate ??
      parsedReq?.spaceHourlyRate ??
      parsedReq?.base_hourly_rate ??
      parsedReq?.base_price_per_hour ??
      (String(parsedReq?.pricing_mode || booking?.booking_mode || '').toLowerCase() === 'exclusive'
        ? booking?.hourly_rate
        : null)
  );
};

/** True when this booking has a listed space rate we can scale with guests or duration. */
export const bookingHasListedSpaceRate = (booking, req = null) => {
  if (!booking) return false;
  const parsedReq = req || parseBookingRequirements(booking);
  const mode = String(
    parsedReq?.pricing_mode || booking?.booking_mode || parsedReq?.booking_type || ''
  ).toLowerCase();
  const isPerPerson = mode === 'per_person' || mode === 'table' || mode === 'per-person';
  if (isPerPerson) {
    return resolveConfirmedPerPersonRate({ booking, req: parsedReq }) != null;
  }
  return resolveConfirmedSpaceHourlyRate({ booking, req: parsedReq }) != null;
};

/** Human-readable label for price breakdown UI. Omit the formula when it does not match the stored hire. */
export const formatPricingBreakdownLabel = (metadata, duration, { storedBaseAmount } = {}) => {
  const dur = duration > 0 ? duration.toFixed(1) : '0.0';
  const computed = computeBaseFromPricingMetadata({
    pricingMode: metadata.pricingMode,
    headcount: metadata.headcount,
    duration,
    perPersonRate: metadata.perPersonRate,
    spaceHourlyRate: metadata.spaceHourlyRate
  });
  const showFormula =
    computed != null &&
    (storedBaseAmount == null || Math.abs(Number(computed) - Number(storedBaseAmount)) < 0.02);

  if (metadata.pricingMode === 'per_person' && metadata.perPersonRate > 0 && metadata.headcount != null) {
    return {
      label: 'Space fee',
      detail: showFormula
        ? `(£${Number(metadata.perPersonRate).toFixed(2)}/person/hr × ${metadata.headcount} people × ${dur} hrs)`
        : null,
      modeLabel: 'Per person'
    };
  }
  if (metadata.spaceHourlyRate > 0) {
    return {
      label: 'Space fee',
      detail: showFormula
        ? `(£${Number(metadata.spaceHourlyRate).toFixed(2)}/hr × ${dur} hrs)`
        : null,
      modeLabel: 'Full venue hire'
    };
  }
  return {
    label: 'Space fee',
    detail: null,
    modeLabel: metadata.pricingMode === 'per_person' ? 'Per person' : 'Full venue hire'
  };
};
